import { useBaseValueRetrieval } from '@/hooks/invoice_reports/useBaseValueRetrieval';
import type { ValueTransformer } from '@/hooks/invoice_reports/useShowSearchValue';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import { useCallback, useMemo } from 'react';

/**
 * Hook that provides ap_payee_ids custom transformer for useShowSearchValue.
 * Resolves payee IDs to display names, with special handling for instant payees.
 * When instant payee is enabled and a payee ID is not found in cache,
 * it displays the instant payee label instead of the encrypted ID.
 */
export const useApPayeeTransformer = (
  payeeOptions?: BaseOption[],
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings
) => {
  const { t } = useTranslation();
  const { getApPayeeValue } = useBaseValueRetrieval();

  // Check if instant payee feature is enabled
  const isInstantPayeeEnabled = useMemo(
    () => officeInvoiceReportSettings?.instant_payee_setting?.enabled ?? false,
    [officeInvoiceReportSettings]
  );

  /**
   * Checks if an ID is likely an instant payee ID (encrypted/hashed format)
   * Instant payee IDs are base64-like strings that don't exist in the master payee list
   */
  const isLikelyInstantPayeeId = useCallback(
    (id: string, lookupResult: string): boolean => {
      if (lookupResult !== id) return false;

      return (
        officeInvoiceReportSettings?.instant_payee_setting
          ?.instant_payee_search_option?.id === id
      );
    },
    [officeInvoiceReportSettings]
  );

  const apPayeeTransformer: ValueTransformer = useCallback(
    (value) => {
      // Normalize value to array format
      let ids: string[];
      if (Array.isArray(value)) {
        ids = value;
      } else if (typeof value === 'string') {
        ids = [value];
      } else {
        ids = [];
      }

      return ids.reduce<string[]>((acc, id) => {
        const payeeName = getApPayeeValue(id, payeeOptions);

        if (isInstantPayeeEnabled && isLikelyInstantPayeeId(id, payeeName)) {
          acc.push(t('ap_payee_input_option_label_instant_payee'));
          return acc;
        }

        if (payeeName === id) return acc;

        acc.push(payeeName);
        return acc;
      }, []);
    },
    [
      getApPayeeValue,
      payeeOptions,
      isInstantPayeeEnabled,
      isLikelyInstantPayeeId,
      t,
    ]
  );

  return useMemo(
    () => ({
      ap_payee_ids: apPayeeTransformer,
    }),
    [apPayeeTransformer]
  );
};
